import React, { useContext, useEffect, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { loader } from "graphql.macro";
import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CurrentUser from "../components/CurrentUser";
import { LangContext, supportedLanguages } from "../I18nProvider";
import { useQuery } from "@apollo/client";
import {
  ENTERPRISE_CONTACT,
  MAX_SEATS,
  addonLookupKey,
  clearPendingCheckout,
  formatEuros,
  parseCheckout,
  planLookupKey,
  pricesByKey,
  savePendingCheckout,
} from "../lib/plans";

const CREATE_CHECKOUT_SESSION = loader(
  "../schema/mutation/CreateCheckoutSession.graphql"
);
const GET_PLAN_PRICES = loader("../schema/query/GetPlanPrices.graphql");

// The entry point of the Subscribe buttons of kizwork.com/pricing (and of the
// pricing table of the app), see §4 of
// docs/Kizwork-Specification-Stripe-2026-11-01.pdf:
// /billing/checkout?price=<lookup_key>[&addon=voice_trust_plus_<interval>][&lang=fr|en][&org=<id>]
// It asks to register or log in first (the offer is kept), then for the
// organization and the number of seats (1 to 250), and goes to the Stripe
// Checkout with the plan and, with Voice Trust+, the add-on for as many seats.
// A wrong combination goes back to the pricing table.
const BillingCheckout = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const choice = parseCheckout(params.get("price"), params.get("addon"));
  const { setLanguage } = useContext(LangContext);
  const lang = params.get("lang");

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang)) {
      // kept for the next pages too, and for the language detected while the
      // provider loads, which would win otherwise
      localStorage.setItem("lang", lang);
      setLanguage(lang);
    }
    // only the language of the link, once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  if (!choice) return <Redirect to="/pricing" />;

  return (
    <CurrentUser>
      {(currentUser) => {
        if (!currentUser) {
          const path = location.pathname + location.search;
          // the Google login comes back on the home page, see Home.tsx
          savePendingCheckout(path);
          window.location.href = `/users/register?return_to=${encodeURIComponent(
            path
          )}`;
          return null;
        }
        return (
          <CheckoutForm
            user={currentUser}
            choice={choice}
            organizationId={params.get("org")}
          />
        );
      }}
    </CurrentUser>
  );
};

interface FormProps {
  user: User;
  choice: NonNullable<ReturnType<typeof parseCheckout>>;
  organizationId: string | null;
}

const CheckoutForm: React.FC<FormProps> = ({
  user,
  choice,
  organizationId,
}) => {
  const intl = useIntl();
  const { data } = useQuery<{ planPrices: PlanPrice[] }>(GET_PLAN_PRICES);
  const prices = pricesByKey(data?.planPrices);
  const [createCheckoutSession, { loading }] = useMutation(
    CREATE_CHECKOUT_SESSION
  );
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => clearPendingCheckout(), []);

  // the organizations the user owns, only the owner subscribes
  const owned = user.organizations.filter((o) => o.role === "OWNER");
  const available = owned.filter((o) => o.plan === "FREE");
  const [selected, setSelected] = useState<string>(
    () =>
      available.find((o) => o.id === organizationId)?.id ??
      available[0]?.id ??
      ""
  );
  const [seats, setSeats] = useState("1");

  const priceKey = planLookupKey(choice.plan, choice.interval);
  const addonKey = choice.addon ? addonLookupKey(choice.interval) : null;
  const unit = prices[priceKey] + (addonKey ? prices[addonKey] : 0);
  const seatsNumber = parseInt(seats, 10);
  const tooMany = seatsNumber > MAX_SEATS;
  const seatsValid = seatsNumber >= 1 && !tooMany;
  const euros = (cents: number) => formatEuros(intl.locale, cents / 100);
  const planName = choice.plan === "starter" ? "Starter" : "Pro Security";

  const submit = async () => {
    setError(null);
    try {
      const result = await createCheckoutSession({
        variables: {
          organizationId: selected,
          price: priceKey,
          addon: addonKey,
          quantity: seatsNumber,
        },
      });
      const url = result.data?.createCheckoutSession.url;
      if (url) {
        setRedirecting(true);
        window.location.href = url;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mb: 6 }}>
      <Typography component="h1" sx={{ fontSize: "1.8rem", fontWeight: 700 }}>
        <FormattedMessage
          id="checkout.title"
          defaultMessage="Subscribe to {plan}"
          values={{
            plan: choice.addon ? `${planName} + Voice Trust+` : planName,
          }}
        />
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        {choice.interval === "monthly" ? (
          <FormattedMessage
            id="checkout.unit-monthly"
            defaultMessage="{price} per user per month, excl. VAT, billed monthly"
            values={{ price: euros(unit) }}
          />
        ) : (
          <FormattedMessage
            id="checkout.unit-yearly"
            defaultMessage="{price} per user per year, excl. VAT, billed once a year"
            values={{ price: euros(unit) }}
          />
        )}
      </Typography>

      {owned.length === 0 ? (
        <Alert severity="warning">
          <FormattedMessage
            id="checkout.not-owner"
            defaultMessage="Only the owner of an organization can subscribe it, and you don't own any."
          />
        </Alert>
      ) : available.length === 0 ? (
        <Alert severity="info">
          <FormattedMessage
            id="checkout.already-subscribed"
            defaultMessage="Your organization already has a plan: change it from <a>its page</a>."
            values={{
              a: (...chunks: React.ReactNode[]) => (
                <a href={`/manage-organization?org=${owned[0].id}`}>{chunks}</a>
              ),
            }}
          />
        </Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {available.length > 1 && (
            <FormControl>
              <InputLabel id="checkout-organization">
                <FormattedMessage
                  id="checkout.organization"
                  defaultMessage="Organization"
                />
              </InputLabel>
              <Select
                labelId="checkout-organization"
                label={intl.formatMessage({
                  id: "checkout.organization",
                  defaultMessage: "Organization",
                })}
                value={selected}
                onChange={(event) => setSelected(event.target.value)}
              >
                {available.map((organization) => (
                  <MenuItem key={organization.id} value={organization.id}>
                    {organization.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            type="number"
            label={intl.formatMessage({
              id: "checkout.seats",
              defaultMessage: "Number of users (seats)",
            })}
            value={seats}
            onChange={(event) => setSeats(event.target.value)}
            inputProps={{ min: 1, max: MAX_SEATS }}
            helperText={
              choice.addon
                ? intl.formatMessage({
                    id: "checkout.seats-help-addon",
                    defaultMessage:
                      "Voice Trust+ is for as many users as the plan.",
                  })
                : intl.formatMessage({
                    id: "checkout.seats-help",
                    defaultMessage:
                      "Every user costs the same. You can change it later.",
                  })
            }
          />
          {tooMany && (
            <Alert severity="info">
              <FormattedMessage
                id="checkout.too-many-seats"
                defaultMessage="More than {max} users? <a>Talk to us about Enterprise Fortress</a>."
                values={{
                  max: MAX_SEATS,
                  a: (...chunks: React.ReactNode[]) => (
                    <a href={ENTERPRISE_CONTACT}>{chunks}</a>
                  ),
                }}
              />
            </Alert>
          )}
          {seatsValid && (
            <Typography sx={{ fontWeight: 600 }}>
              {choice.interval === "monthly" ? (
                <FormattedMessage
                  id="checkout.total-monthly"
                  defaultMessage="Total: {total} excl. VAT per month"
                  values={{ total: euros(unit * seatsNumber) }}
                />
              ) : (
                <FormattedMessage
                  id="checkout.total-yearly"
                  defaultMessage="Total: {total} excl. VAT per year"
                  values={{ total: euros(unit * seatsNumber) }}
                />
              )}
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button href="/pricing" color="secondary">
              <FormattedMessage
                id="checkout.back-to-plans"
                defaultMessage="Back to the plans"
              />
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={!seatsValid || !selected || loading || redirecting}
              onClick={submit}
            >
              {loading || redirecting ? (
                <CircularProgress size={20} />
              ) : (
                <FormattedMessage
                  id="checkout.continue"
                  defaultMessage="Continue to payment"
                />
              )}
            </Button>
          </Box>
          <Typography sx={{ fontSize: ".85rem", color: "text.secondary" }}>
            <FormattedMessage
              id="checkout.vat-note"
              defaultMessage="The VAT is calculated on the payment page from your country and your VAT number."
            />
            {choice.interval === "yearly" && (
              <>
                {" "}
                <FormattedMessage
                  id="checkout.sepa-note"
                  defaultMessage="An annual plan can also be paid by SEPA Direct Debit."
                />
              </>
            )}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

// /billing/success and /billing/cancel, where the Stripe Checkout comes back:
// the page of the organization, which waits for the webhook to show the new
// plan
export const BillingReturn: React.FC<{ status: "complete" | "canceled" }> = ({
  status,
}) => {
  const params = new URLSearchParams(useLocation().search);
  const org = params.get("org");
  const target = new URLSearchParams({ checkout: status });
  if (org) target.set("org", org);
  return <Redirect to={`/manage-organization?${target.toString()}`} />;
};

export default BillingCheckout;
